import type { Project } from "@proof/core";
import { effectiveBackground } from "@proof/core";
import { Button } from "./components";
export function ProjectSummary({
  project,
  onGo,
  onExport,
  onDetails,
  canEdit,
  canExport,
  canProof,
  canNavigate = () => true,
}: {
  project: Project;
  onGo: (module: string) => void;
  onExport: () => void;
  onDetails: () => void;
  canEdit: boolean;
  canExport: boolean;
  canProof: boolean;
  canNavigate?: (destination: string) => boolean;
}) {
  const nodes = project.pages.flatMap((page) => page.nodes);
  const overrides = project.parts.reduce(
    (total, part) => total + Object.keys(part.overrides).length,
    0,
  );
  const calculated = project.fields.filter((field) => field.calculation).length;
  return (
    <div className="project-summary">
      <div className="page-intro">
        <div>
          <span className="eyebrow">CURRENT PROJECT</span>
          <h1>Project summary</h1>
          <p>
            {project.name} · {project.status} · revision {project.revision}
          </p>
        </div>
        <div className="button-row">
          <Button
            disabled={!canProof || !canNavigate("proof")}
            onClick={() => onGo("proof")}
          >
            Open live proof
          </Button>
          <Button kind="primary" disabled={!canExport} onClick={onExport}>
            Export project
          </Button>
        </div>
      </div>
      <div className="integration-detail-grid">
        <section className="panel padded">
          <h2>{project.name}</h2>
          <p>{project.description || "No description yet."}</p>
          <dl className="project-summary-facts">
            <dt>Project ID</dt>
            <dd style={{ overflowWrap: "anywhere" }}>{project.id}</dd>
            <dt>Revision</dt>
            <dd>{project.revision}</dd>
            <dt>Status</dt>
            <dd>{project.status}</dd>
            <dt>Instructions</dt>
            <dd>{project.instructions || "None configured"}</dd>
          </dl>
          <Button disabled={!canEdit} onClick={onDetails}>
            Edit project details
          </Button>
          <Button
            disabled={!canNavigate("integrations")}
            onClick={() => onGo("integrations")}
          >
            JavaScript integration
          </Button>
        </section>
        <section className="panel padded">
          <h2>Variables & forms</h2>
          <dl className="project-summary-facts">
            <dt>Variables</dt>
            <dd>{project.fields.length}</dd>
            <dt>Calculated variables</dt>
            <dd>{calculated}</dd>
            <dt>Form groups / tabs</dt>
            <dd>
              {project.groups.length} / {project.tabs.length}
            </dd>
            <dt>Reusable forms / instances</dt>
            <dd>
              {project.forms.length} / {project.formInstances.length}
            </dd>
            <dt>Visibility triggers</dt>
            <dd>{project.triggers.length}</dd>
          </dl>
          <Button
            disabled={!canEdit || !canNavigate("configuration")}
            onClick={() => onGo("configuration")}
          >
            Edit fields & variables
          </Button>
        </section>
        <section className="panel padded">
          <h2>Pages & design</h2>
          <p>
            {project.pages.length} pages · {nodes.length} layers ·{" "}
            {project.styles.length} text styles
          </p>
          <ul>
            {project.pages.map((page) => (
              <li key={page.id}>
                <strong>{page.name}</strong>: {page.width ?? "Auto"} ×{" "}
                {page.height ?? "Auto"} pt · {page.frame?.name || "Rectangle"} ·{" "}
                {effectiveBackground(project, page) || "Transparent"} ·{" "}
                {page.nodes.length} layers
              </li>
            ))}
          </ul>
          <Button
            disabled={!canEdit || !canNavigate("designer")}
            onClick={() => onGo("designer")}
          >
            Open document designer
          </Button>
        </section>
        <section className="panel padded">
          <h2>Parts & overrides</h2>
          <p>
            {project.parts.length} parts · {overrides} explicit variable
            overrides
          </p>
          <p>
            Only overridden variables are stored on a part. Other variables
            inherit the main project configuration.
          </p>
          {project.parts.length ? (
            <ul>
              {project.parts.map((part) => (
                <li key={part.id}>
                  <strong>{part.name}</strong> ({part.sku || "No SKU"}):{" "}
                  {Object.keys(part.overrides).length} overridden variable
                  {Object.keys(part.overrides).length === 1 ? "" : "s"}
                </li>
              ))}
            </ul>
          ) : (
            <p>No part overrides configured.</p>
          )}
          <Button
            disabled={!canEdit || !canNavigate("configuration")}
            onClick={() => onGo("configuration")}
          >
            Configure parts
          </Button>
        </section>
        <section className="panel padded">
          <h2>Datasets & resources</h2>
          <p>
            {project.datasets.length} datasets · {project.fontManifest.length}{" "}
            managed fonts · {project.resourceSnapshots.length} linked resource
            snapshots
          </p>
          {project.datasets.length ? (
            <ul>
              {project.datasets.map((dataset) => (
                <li key={dataset.id}>
                  {dataset.name}:{" "}
                  {dataset.kind === "choices"
                    ? dataset.choices.length + " choices"
                    : dataset.rows.length + " rows"}
                </li>
              ))}
            </ul>
          ) : (
            <p>No project datasets configured.</p>
          )}
          <ul>
            {project.fontManifest.map((face) => (
              <li key={face.id}>{face.name}</li>
            ))}
          </ul>
          <Button
            disabled={!canEdit || !canNavigate("resources-datasets")}
            onClick={() => onGo("resources-datasets")}
          >
            Configure datasets
          </Button>
        </section>
        <section className="panel padded">
          <h2>Output profiles</h2>
          <ul>
            {project.outputs.map((output) => (
              <li key={output.id}>
                <strong>{output.name}</strong>: {output.format.toUpperCase()} ·{" "}
                {output.colorSpace.toUpperCase()} · {output.dpi} DPI ·{" "}
                {output.includeProduction !== false
                  ? "With production settings"
                  : "Production settings excluded"}
                {output.preserveImportedPaint || output.preserveImportedPdf
                  ? " · Source colors preserved"
                  : ""}
              </li>
            ))}
          </ul>
          <Button
            disabled={!canEdit || !canNavigate("outputs")}
            onClick={() => onGo("outputs")}
          >
            Configure output profiles
          </Button>
        </section>
      </div>
    </div>
  );
}
